import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { prefersReducedMotion, startViewTransition, supportsViewTransitions } from '@theme/utilities';

/** Latest variant selection per section, so slow responses never replace newer ones. @type {WeakMap<Element, symbol>} */
const latestSwap = new WeakMap();

/**
 * Resolves once the first image of a fetched gallery is decoded, or after a short timeout on slow connections.
 * @param {Element} gallery - The fetched media gallery.
 * @returns {Promise<void>}
 */
function decodeFirstImage(gallery) {
  const source = gallery.querySelector('img');
  if (!source) return Promise.resolve();

  const image = new Image();
  const sizes = source.getAttribute('sizes');
  const srcset = source.getAttribute('srcset');
  if (sizes && sizes !== 'auto') image.sizes = sizes;
  if (srcset) image.srcset = srcset;
  image.src = source.getAttribute('src') ?? '';

  return Promise.race([image.decode().catch(() => {}), new Promise((resolve) => setTimeout(resolve, 1200))]);
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const target = this.closest('.shopify-section, dialog');
    const request = Symbol('media-swap');
    if (target) latestSwap.set(target, request);

    event.promise
      .then(async ({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // WallyFrame: keep the current picture until the next one is decoded, then crossfade.
        // A newer selection, or a gallery that was already replaced, wins over this one.
        await decodeFirstImage(newMediaGallery);
        if (!this.isConnected || (target && latestSwap.get(target) !== request)) return;

        if (!(newMediaGallery instanceof HTMLElement) || prefersReducedMotion()) {
          this.replaceWith(newMediaGallery);
          return;
        }

        if (!supportsViewTransitions()) {
          newMediaGallery.classList.add('wf-media-enter');
          this.replaceWith(newMediaGallery);
          return;
        }

        this.style.setProperty('view-transition-name', 'wf-media');
        await startViewTransition(() => {
          this.style.removeProperty('view-transition-name');
          newMediaGallery.style.setProperty('view-transition-name', 'wf-media');
          this.replaceWith(newMediaGallery);
        });
        newMediaGallery.style.removeProperty('view-transition-name');
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
